/* ==========================================================================
   Kid Rock site behaviour — assets/kr-site.js
   Loaded with defer from sections/kr-topbar.liquid.
   Modules: hamburger menu, release countdown, store category filter,
   videos "load more", video lightbox, dialogs (release links).
   Re-inits on theme editor section reloads.
   ========================================================================== */
(() => {
  const DEBUG = false; // flip to true to log init + filter results
  const log = (...a) => { if (DEBUG) console.log('[kr-site]', ...a); };

  /* ---- Hamburger menu ---- */
  function initMenus(root) {
    root.querySelectorAll('.menu-wrap').forEach((wrap) => {
      if (wrap.dataset.krInit) return;
      wrap.dataset.krInit = '1';
      const button = wrap.querySelector('.menu-button');
      const menu = wrap.querySelector('.menu-list');
      if (!button || !menu) return;
      const close = () => { menu.hidden = true; button.setAttribute('aria-expanded', 'false'); };
      button.addEventListener('click', () => {
        menu.hidden = !menu.hidden;
        button.setAttribute('aria-expanded', String(!menu.hidden));
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
      document.addEventListener('click', (e) => { if (!menu.hidden && !e.target.closest('.menu-wrap')) close(); });
      log('menu init');
    });
  }

  /* ---- Countdown ----
     Release time comes from the section setting (ISO 8601 with offset).
     Invalid date = element left empty and a console warning. */
  function initCountdowns(root) {
    root.querySelectorAll('[data-kr-countdown]').forEach((el) => {
      if (el._krTimer) clearInterval(el._krTimer);
      const releaseTime = Date.parse(el.dataset.release);
      if (Number.isNaN(releaseTime)) { console.warn('[kr-site] Invalid release date:', el.dataset.release); return; }
      const outNow = el.dataset.outNow || 'OUT NOW';
      const update = () => {
        const remaining = releaseTime - Date.now();
        if (remaining <= 0) { el.textContent = outNow; clearInterval(el._krTimer); return; }
        const s = Math.floor(remaining / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        el.textContent = d + 'D ' + String(h).padStart(2, '0') + 'H ' + String(m).padStart(2, '0') + 'M ' + String(s % 60).padStart(2, '0') + 'S';
      };
      update();
      el._krTimer = setInterval(update, 1000);
      log('countdown init', el.dataset.release);
    });
  }

  /* ---- Store filter ----
     Mirrors the export's /store?collection=<handle> routes.
     Runs across EVERY [data-kr-store-grid] on the page (main + overflow
     sections), so it always scopes to document, not the reloaded section.
     Cards carry data-categories="unisex apparel"; no param (or "all" /
     "new-home-page") shows everything. Without JS every card shows. */
  function initStore() {
    const grids = document.querySelectorAll('[data-kr-store-grid]');
    if (!grids.length) return;
    const param = (new URLSearchParams(window.location.search).get('collection') || '').toLowerCase();
    const showAll = !param || param === 'all' || param === 'new-home-page';
    let labels = {};
    const labelSrc = document.querySelector('[data-kr-store-grid][data-labels]');
    try { labels = JSON.parse((labelSrc && labelSrc.dataset.labels) || '{}'); } catch (e) { console.warn('[kr-site] Bad labels JSON', e); }
    const cards = document.querySelectorAll('[data-kr-store-grid] .product-card');
    let visible = 0;
    cards.forEach((card) => {
      const cats = (card.dataset.categories || '').split(/\s+/);
      const match = showAll || cats.includes(param);
      card.toggleAttribute('data-kr-hidden', !match);
      if (match) visible += 1;
    });
    const intro = document.querySelector('[data-kr-store-intro]');
    if (intro && !showAll) intro.textContent = labels[param] || param.replace(/-/g, ' ').toUpperCase();
    const empty = document.querySelector('[data-kr-store-empty]');
    if (empty) empty.hidden = visible > 0;
    // One submenu open at a time; click outside closes
    const menu = document.querySelector('[data-kr-store-menu]');
    if (menu && !menu.dataset.krInit) {
      menu.dataset.krInit = '1';
      menu.querySelectorAll('details').forEach((d) => d.addEventListener('toggle', () => {
        if (d.open) menu.querySelectorAll('details[open]').forEach((o) => { if (o !== d) o.open = false; });
      }));
      document.addEventListener('click', (e) => {
        if (!e.target.closest('[data-kr-store-menu]')) menu.querySelectorAll('details[open]').forEach((o) => { o.open = false; });
      });
    }
    log('store filter', { param, showAll, visible, total: cards.length, grids: grids.length });
  }

  /* ---- Load more (videos) ----
     A primary list plus every [data-kr-continued] list directly after it
     (overflow sections for lists over Shopify's 50-block cap) form one
     group. The primary section's button reveals hidden items across the
     whole group in DOM order, and is moved below the last list so it never
     sits between two grids. Scoped to document because groups span sections. */
  function getVideoGroup(primary) {
    const all = Array.from(document.querySelectorAll('[data-kr-video-list]'));
    const lists = [primary];
    for (let i = all.indexOf(primary) + 1; i < all.length; i += 1) {
      if (!all[i].hasAttribute('data-kr-continued')) break;
      lists.push(all[i]);
    }
    return lists;
  }

  function initLoadMore() {
    document.querySelectorAll('[data-kr-load-more]').forEach((btn) => {
      if (btn.dataset.krInit) return;
      const primary = document.getElementById(btn.getAttribute('aria-controls'));
      if (!primary) { console.warn('[kr-site] Load more target missing:', btn.getAttribute('aria-controls')); return; }
      btn.dataset.krInit = '1';
      const step = parseInt(btn.dataset.step, 10) || 12;
      const lists = getVideoGroup(primary);
      const hiddenItems = () => lists.flatMap((l) => Array.from(l.querySelectorAll('[data-kr-hidden]')));
      const last = lists[lists.length - 1];
      if (last !== primary) last.after(btn);
      btn.hidden = hiddenItems().length === 0;
      btn.addEventListener('click', () => {
        hiddenItems().slice(0, step).forEach((el) => el.removeAttribute('data-kr-hidden'));
        btn.hidden = hiddenItems().length === 0;
      });
      log('load more init', { lists: lists.length, hidden: hiddenItems().length, step });
    });
  }

  /* ---- Video lightbox ----
     Cards with [data-kr-video] open a pop-up player instead of navigating.
     The card's href stays the real video URL, so no-JS, middle-click and
     ctrl/cmd/shift-click still open it in a new tab (normal link behaviour).
     One modal is built on first use and shared by every gallery on the page.
     The iframe is removed on close so playback stops. */
  let videoModal = null;
  let lastFocus = null;

  function buildVideoModal() {
    const modal = document.createElement('div');
    modal.className = 'kr-video-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video player');
    modal.innerHTML =
      '<div class="kr-video-modal__backdrop" data-kr-modal-close></div>' +
      '<div class="kr-video-modal__dialog">' +
        '<button type="button" class="kr-video-modal__close" aria-label="Close video" data-kr-modal-close>' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
            '<path d="M5 5L19 19M19 5L5 19"/>' +
          '</svg>' +
        '</button>' +
        '<div class="kr-video-modal__frame"></div>' +
        '<a class="kr-video-modal__fallback" href="#" target="_blank" rel="noopener">Video not playing? Open it in a new tab</a>' +
      '</div>';
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-kr-modal-close]')) closeVideoModal();
    });
    modal.addEventListener('keydown', (e) => {
      // Keep Tab focus inside the dialog (close button <-> fallback link)
      if (e.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll('button, a[href], iframe'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    document.body.appendChild(modal);
    return modal;
  }

  function openVideoModal(card) {
    if (!videoModal) videoModal = buildVideoModal();
    const iframe = document.createElement('iframe');
    iframe.src = card.dataset.krEmbed;
    iframe.title = card.dataset.krTitle || 'Video';
    iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    // YouTube rejects embeds that send no referrer (Error 153)
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    videoModal.querySelector('.kr-video-modal__frame').replaceChildren(iframe);
    videoModal.querySelector('.kr-video-modal__fallback').href = card.href;
    lastFocus = document.activeElement;
    videoModal.hidden = false;
    document.documentElement.classList.add('kr-modal-open');
    videoModal.querySelector('.kr-video-modal__close').focus();
    log('video open', card.dataset.krEmbed);
  }

  function closeVideoModal() {
    if (!videoModal || videoModal.hidden) return;
    videoModal.querySelector('.kr-video-modal__frame').replaceChildren();
    videoModal.hidden = true;
    document.documentElement.classList.remove('kr-modal-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    log('video close');
  }

  function initVideoModal() {
    // Delegated once on document: covers every gallery, LOAD MORE reveals
    // and theme editor section reloads without re-binding.
    if (document.documentElement.dataset.krVideoModal) return;
    document.documentElement.dataset.krVideoModal = '1';
    document.addEventListener('click', (e) => {
      const card = e.target.closest('a[data-kr-video]');
      if (!card || !card.dataset.krEmbed) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      openVideoModal(card);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeVideoModal();
    });
  }

  /* ---- Dialogs (release links pop-up, sections/kr-single.liquid) ----
     [data-kr-dialog-open="<dialog id>"] opens that native <dialog> as a modal.
     Esc closes natively; the x is a method="dialog" form button; a click on
     the backdrop closes it (the dialog has no padding, so a click whose target
     is the <dialog> itself can only be on the backdrop).
     Theme editor: selecting a link block opens the dialog so it's editable. */
  function initDialogs() {
    if (document.documentElement.dataset.krDialogs) return;
    document.documentElement.dataset.krDialogs = '1';
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-kr-dialog-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.krDialogOpen);
        if (!dialog || typeof dialog.showModal !== 'function') {
          console.warn('[kr-site] Dialog missing or unsupported:', opener.dataset.krDialogOpen);
          return;
        }
        if (!dialog.open) dialog.showModal();
        log('dialog open', dialog.id);
        return;
      }
      if (e.target instanceof HTMLDialogElement && e.target.classList.contains('kr-dialog') && e.target.open) {
        e.target.close();
        log('dialog close (backdrop)', e.target.id);
      }
    });
    document.addEventListener('shopify:block:select', (e) => {
      const dialog = e.target.closest && e.target.closest('dialog.kr-dialog');
      if (dialog && !dialog.open) dialog.showModal();
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      const dialog = e.target.closest && e.target.closest('dialog.kr-dialog');
      if (dialog && dialog.open) dialog.close();
    });
  }

  function init(root = document) {
    initMenus(root);
    initCountdowns(root);
    initStore();
    initLoadMore();
    initVideoModal();
    initDialogs();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
